// Kích thước trang PDF (px) — 96dpi ~ Chrome/Puppeteer
export const PAGE_W = Math.round((210 / 25.4) * 96); // ≈ 794
export const PAGE_H = Math.round((297 / 25.4) * 96); // ≈ 1123

// Đọc ảnh trong /public thành base64
export async function toBase64FromPublic(pathFromPublic) {
  const url = new URL(pathFromPublic, window.location.origin).href;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Cannot fetch " + url);
  const blob = await res.blob();

  return await new Promise(function (resolve, reject) {
    const reader = new FileReader();
    reader.onloadend = function () {
      resolve(String(reader.result)); // data:image/...
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Biến tất cả <img> thành absolute URL (trừ data:)
export function absolutifyImgs(root) {
  root.querySelectorAll("img").forEach(function (img) {
    var src = img.getAttribute("src") || img.src;
    if (!src || src.startsWith("data:")) return;
    try {
      img.src = new URL(src, window.location.origin).href;
    } catch (e) {}
  });
}

// Ép các logo (.profile-logo) thành base64 để chắc chắn hiển thị trong Puppeteer
// ⚠️ Selector "img.profile-logo" — khớp với class trong DetailForm & DynamicForm
export async function base64ifyLogos(
  root,
  placeholder = "/images/default-profile.png"
) {
  const logos = root.querySelectorAll("img.profile-logo"); // class, không phải id
  for (const el of Array.from(logos)) {
    const img = el;
    const src = img.getAttribute("src") || "";
    if (!src || src.startsWith("data:")) continue;
    try {
      const u = new URL(src, window.location.origin);
      if (u.origin === window.location.origin) {
        img.src = await toBase64FromPublic(u.pathname);
      } else {
        // Ảnh ngoài domain — giữ absolute URL
        img.src = u.href;
      }
    } catch (e) {
      img.src = new URL(placeholder, window.location.origin).href;
    }
  }
}

/**
 * Build HTML để in PDF + overlay watermark
 * watermarkMode:
 *  - "fixed": RECOMMENDED. Dùng position:fixed ở cấp <body>, Chromium sẽ vẽ lại ở MỖI TRANG.
 *  - "absolute": chỉ phủ vùng nội dung (không lặp theo trang).
 */
export function buildPrintHTML(innerHtml, watermarkMode = "fixed") {
  const watermarkAbsoluteCSS = `
    /* ABSOLUTE: phủ vùng nội dung (KHÔNG LẶP THEO TRANG) */
    #contentToPrint { position: relative; }
    .wm-overlay {
      position: absolute;
      inset: 0;
      background-size: 90%;
      background-repeat: no-repeat;
      background-position: center;
      opacity: 0.12;
      pointer-events: none;
      z-index: 10000;
    }
  `;

  const watermarkFixedCSS = `
    /* FIXED: Chromium/Puppeteer render lại ở MỖI TRANG */
    .wm-overlay-fixed {
      position: fixed;
      inset: 0;
      padding: 0.85cm 1cm 1.2cm; /* Khớp với @page margin để không bị khuất mép */
      background-image: var(--wm-url);
      background-size: 95%;
      background-repeat: no-repeat;
      background-position: center;
      background-origin: content-box;
      opacity: 0.12;
      pointer-events: none;
      z-index: 2147483647;
    }
    @media print {
      .wm-overlay-fixed { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  const overlayHtml =
    watermarkMode === "absolute"
      ? `<div class="wm-overlay" id="wm-layer"></div>`
      : `<div class="wm-overlay-fixed" id="wm-layer"></div>`;

  return `<!doctype html>
<html lang="vi">
<head>
  <meta charset="utf-8"/>
  <title>PDF</title>
  <style>
    :root{
      --page-w:${PAGE_W}px;
      --page-h:${PAGE_H}px;
    }
    *{box-sizing:border-box;margin:0;padding:0}
    html,body{background:#fff;color:#111;font-family:Arial,"Times New Roman",serif}
    @page{ size:${PAGE_W}px ${PAGE_H}px; margin: 0.85cm 1cm 1.2cm; }
    body{ -webkit-print-color-adjust: exact; print-color-adjust: exact; }

    .pdf-root{
      width: 100%;
      margin:0 auto;
      line-height:1.35;
      font-size:13px;
      display: block !important;
    }

    /* Force Centering */
    .profile-container { 
      border: none !important; 
      width: 100% !important;
      display: block !important;
    }
    
    .profile-heading {
      width: 100% !important;
      text-align: center !important;
      margin: 0 0 8px 0 !important;
      display: block !important;
    }

    .profile-meta-grid { 
      display: grid !important;
      grid-template-columns: repeat(3, 1fr) !important;
      margin: 0 auto 12px auto !important;
      gap: 6px 8px !important;
      width: 100% !important;
    }
    
    .meta-item {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      gap: 6px !important;
      width: 100% !important;
    }
    
    .meta-value {
      color: #000;
    }

    /* Logo centering */
    .w-full.flex.items-center.justify-center {
      display: flex !important;
      justify-content: center !important;
      width: 100% !important;
      margin: 8px 0 !important;
    }

    .profile-logo {
      height: 5.5rem !important;
      max-height: 88px !important;
      margin: 0 auto !important;
      display: block !important;
    }

    .dynamic-content-wrapper {
      width: 100% !important;
      display: block !important;
      margin: 8px 0 12px !important;
    }

    /* Spacing for sections in PDF */
    .space-y-6 > div {
       margin-bottom: 10px !important;
    }

    /* Utility classes fallback */
    .w-full{width:100%} .text-center{text-align:center} .uppercase{text-transform:uppercase}
    .border-b{border-bottom:none !important} .p-4{padding:.4rem !important} .border{border:1px solid #e5e7eb !important} .rounded-lg{border-radius:.35rem !important} .overflow-hidden{overflow:hidden !important}
.my-6{margin-top:.35rem !important;margin-bottom:.35rem !important}
.mt-10{margin-top:.45rem !important} .mb-8{margin-bottom:.6rem !important} .mb-6{margin-bottom:.45rem !important} .mt-8{margin-top:.6rem !important} .mt-16{margin-top:1rem !important}
    .flex{display:flex} .items-center{align-items:center} .justify-center{justify-content:center}
    .gap-4{gap:.5rem} .mr-2{margin-right:4px} .gap-y-6{row-gap:.4rem} .mb-4{margin-bottom:.45rem}
    .font-semibold{font-weight:600} .whitespace-nowrap{white-space:nowrap}
    .table-heading{color:#fff;background:#0457C2;padding:7px 8px !important}
    .rounded-t-lg{border-top-left-radius:.35rem;border-top-right-radius:.35rem}
    .grid{display:grid} .grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}
    @media (min-width:1280px){ .xl\\:grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))} }

    .text-\\[1\\.5rem\\]{font-size:1.1rem}
    .text-\\#0457C2{color:#0457C2}
    .font-sf-semibold{font-weight:600} .font-sf-medium{font-weight:600}
    .text-gray-400{color:#9ca3af !important} .italic{font-style:italic !important}

    table{width:100% !important;border-collapse:collapse;font-size:12px;margin-bottom:8px;border:none !important}
    thead tr{background:#e5e7eb;height:34px}
    th,td{padding:6px 8px !important;text-align:left;border:none !important}
    tbody tr{background:transparent !important; border: none !important}
    tbody tr:hover{background:transparent !important; border: none !important}
    th,td{border: none !important}
    .profile-logo{height:5.5rem;object-fit:contain;display:block}
    .hide-section, #download-btn { display: none !important; }
    .pdf-only-row { display: table-row !important; }
    .profile-download-footer{
      margin-top:12px !important;
      text-align:center !important;
      color:#333 !important;
      font-size:11.5px !important;
      line-height:1.35 !important;
      page-break-inside:avoid;
    }

    ${watermarkMode === "absolute" ? watermarkAbsoluteCSS : watermarkFixedCSS}

    .profile-heading{
      width:100%; text-align:center;
      font-family:Arial,"Times New Roman",serif;
      font-weight:600; color:#0457C2; font-size:1.1rem; text-transform:uppercase;
      padding:.35rem 0; border-bottom:1px solid #e5e7eb;
    }
  </style>
</head>
<body>
  <div class="pdf-root" id="contentToPrint">
    ${innerHtml}
  </div>
  ${overlayHtml}
</body>
</html>`;
}
